/**
 * AFS Legal OS — Research Assistant — Query/Findings Generator ("Supo")
 *
 * Roadmap ref: 6c (Research Assistant) — the entire core engine; everything
 * else in section 6 is UI/storage around this one function.
 *
 * REDESIGN — query-only, manual paste-back (combined build plan, Part 1).
 * `runResearchTurn()` is gone. In its place, three functions split what used
 * to be one model call into two, plus a final synthesis step:
 *
 *   - `generateNextQuery(session)` — ONE model call, produces ONLY the next
 *     query (derived from the problem statement on turn 1, from the PRIOR
 *     entry's findings on every turn after). No findings, no exhaustion
 *     verdict yet — the entry is appended with `status: 'awaiting_findings'`
 *     and empty findings. This is as far as Supo goes without counsel.
 *
 *   - `submitFindings(session, rawText)` — counsel pastes in whatever they
 *     actually found (a case, a statute, a textbook passage, search-result
 *     text — anything). ONE model call synthesizes that raw paste into
 *     grounded `findings` for the awaiting entry, and — separately — judges
 *     `exhausted` against the ORIGINAL problem statement (not just this
 *     entry's query). Sets `status: 'complete'`.
 *
 *   - `generateFinalReport(session)` — walks every complete entry and
 *     produces 3–4 pages of prose with OSCOLA footnotes for anything in the
 *     pasted research that is citable. Claims that were never sourced in
 *     what counsel pasted stay uncited in the prose — no inline flag, no
 *     invented footnote. Writes `session.final_report`.
 *
 * WHY SPLIT THIS WAY — the old `runResearchTurn()` had Supo both pick the
 * query AND invent the findings for it in one shot, reasoning from general
 * knowledge with no verification step. That's fine for a rough first pass
 * but wrong for anything counsel will file: every finding needs to trace
 * back to something counsel actually read. Splitting query generation from
 * findings synthesis forces a human research step in between — Supo asks,
 * counsel goes and looks, counsel pastes back what's real, Supo only ever
 * writes up what was pasted.
 *
 * SAFETY VALVE — `MAX_RESEARCH_TURNS` now gates `generateNextQuery()`
 * specifically (no new query past the cap), not a forced findings turn —
 * there is no such thing as a forced findings turn any more, since findings
 * only ever come from counsel's own paste.
 *
 * NOT YET WIRED — per research/types.ts's file header, there is no
 * persistence layer for research sessions yet. These functions read and
 * return a ResearchSession purely in memory; they do not read or write
 * storage. `generateNextQuery` also does not query the internal
 * library/Vectorize RAG (services/library.ts) — Supo reasons from general
 * legal knowledge when picking a query, same convention as the prior
 * CaseResearch.tsx topic-search flow.
 *
 * Exported as plain async functions — no React/hook dependency — same
 * convention as pass0.ts / clauseRegister.ts / knowledgeTier.ts.
 */

import { callClaude, withRetry, classifyError } from '@/services/api';
import { uid } from '@/utils';
import type { ResearchQueryFinding, ResearchSession } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// SAFETY VALVE — see file header
// ─────────────────────────────────────────────────────────────────────────────

export const MAX_RESEARCH_TURNS = 8;

// ─────────────────────────────────────────────────────────────────────────────
// SHARED RESULT SHAPE
// ─────────────────────────────────────────────────────────────────────────────

interface StepResult {
  /** The session with the change applied (or unchanged — see guard clauses in each function). */
  session: ResearchSession;
  /** True iff the session was actually changed this call. */
  advanced: boolean;
  ok: boolean;
  /** Set only when ok is false. */
  error?: string;
}

export type GenerateNextQueryResult = StepResult;
export type SubmitFindingsResult    = StepResult;

export interface GenerateFinalReportResult {
  session: ResearchSession;
  ok: boolean;
  error?: string;
}

function unchanged(session: ResearchSession, error?: string): StepResult {
  return { session, advanced: false, ok: !error, error };
}

// ─────────────────────────────────────────────────────────────────────────────
// generateNextQuery — query only, no findings
// ─────────────────────────────────────────────────────────────────────────────

interface RawQuery {
  query: string;
}

const QUERY_SYSTEM = `You are Supo, the AFS Legal OS Research Assistant. You work a single
problem statement one turn at a time. Each turn you produce exactly ONE
research query for counsel to go and answer themselves — you do NOT answer
it yourself.

RULES FOR THE QUERY:
- On the FIRST turn (no prior entries), the query is the most natural
  starting angle on the problem statement itself.
- On every turn AFTER the first, the query must be derived from the PRIOR
  turn's findings — following up on a gap, an unresolved thread, a named
  authority worth checking, or the next angle the prior findings surfaced.
  Never simply restate the problem statement or repeat a prior query.
- The query should be specific enough that it reads like something counsel
  could paste into a legal research tool, not a vague restatement of the
  topic.

Do not wrap the JSON in markdown code fences — return raw JSON only.

Return EXACTLY this shape, nothing else:
{
  "query": string
}`;

function buildQueryUserMsg(session: ResearchSession): string {
  const complete = session.entries.filter(e => e.status === 'complete');
  const lines: string[] = [];
  lines.push(`PROBLEM STATEMENT:`);
  lines.push(session.problem_statement.trim());

  if (complete.length === 0) {
    lines.push('');
    lines.push('No completed prior turns — this is turn 1.');
  } else {
    lines.push('');
    lines.push('PRIOR TURNS (oldest first, completed only):');
    complete.forEach((e, i) => {
      lines.push(`Turn ${i + 1} query: ${e.query}`);
      lines.push(`Turn ${i + 1} findings (counsel-sourced): ${e.findings}`);
    });
  }

  return lines.join('\n');
}

function parseQuery(raw: string): RawQuery | null {
  try {
    const stripped = raw.trim()
      .replace(/^```(?:json)?/i, '')
      .replace(/```$/, '')
      .trim();
    const parsed = JSON.parse(stripped) as Record<string, unknown>;
    const query = parsed.query;
    if (typeof query === 'string' && query.trim()) {
      return { query: query.trim() };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Generates the next query and appends it as a new entry with
 * `status: 'awaiting_findings'` and empty findings. Never throws — a failed
 * AI call or a malformed response degrades to `ok: false` with the session
 * returned unchanged.
 *
 * No-ops if the session is not `'active'`, if the last entry is still
 * `'awaiting_findings'` (counsel must submit findings for the current turn
 * before a new one can be generated), or if generating another turn would
 * exceed `MAX_RESEARCH_TURNS`.
 */
export async function generateNextQuery(session: ResearchSession): Promise<GenerateNextQueryResult> {
  if (session.status !== 'active') {
    return unchanged(session);
  }
  if (!session.problem_statement.trim()) {
    return unchanged(session, 'Session has no problem statement');
  }
  const last = session.entries[session.entries.length - 1];
  if (last && last.status === 'awaiting_findings') {
    return unchanged(session, 'Submit findings for the current turn before starting the next one.');
  }
  if (session.entries.length >= MAX_RESEARCH_TURNS) {
    return unchanged(session, `Reached the maximum of ${MAX_RESEARCH_TURNS} research turns.`);
  }

  let raw: RawQuery | null;
  try {
    const { text } = await withRetry(() => callClaude({
      system:      QUERY_SYSTEM,
      userMsg:     buildQueryUserMsg(session),
      maxTokens:   400,
      skipLibrary: true,
    }));
    raw = parseQuery(text);
  } catch (e) {
    return unchanged(session, classifyError(e));
  }

  if (!raw) {
    return unchanged(session, 'Could not parse a query from AI response');
  }

  const now = new Date().toISOString();
  const entry: ResearchQueryFinding = {
    id:                  uid(),
    query:               raw.query,
    findings:            '',
    status:              'awaiting_findings',
    exhausted:           false,
    exhausted_reasoning: '',
    created_at:          now,
  };

  const updatedSession: ResearchSession = {
    ...session,
    entries:    [...session.entries, entry],
    updated_at: now,
  };

  return { session: updatedSession, advanced: true, ok: true };
}

// ─────────────────────────────────────────────────────────────────────────────
// submitFindings — counsel pastes raw research back in
// ─────────────────────────────────────────────────────────────────────────────

interface RawFindings {
  findings:            string;
  exhausted:           boolean;
  exhausted_reasoning: string;
}

const FINDINGS_SYSTEM = `You are Supo, the AFS Legal OS Research Assistant. Counsel has gone and
done the actual research for the query you gave them, and pasted back the
raw material they found (case text, statute text, textbook excerpts, search
results — whatever it is). Your job this turn:

1. Synthesize what counsel pasted into grounded findings that answer the
   query — in your own words, not a copy of the paste. Do not add anything
   that is not supported by what was pasted. If the paste does not actually
   answer the query, say so plainly in the findings rather than padding.
2. Judge, honestly, against the ORIGINAL problem statement (not just this
   turn's query) — has enough now been gathered, across all turns so far
   including this one, to consider the problem statement resolved?
   Default to false unless you can articulate specifically what would be
   redundant about another turn.

exhausted_reasoning is required regardless of the verdict — one or two
sentences on why you did or didn't consider the problem resolved as of this
turn.

Do not wrap the JSON in markdown code fences — return raw JSON only.

Return EXACTLY this shape, nothing else:
{
  "findings": string,
  "exhausted": boolean,
  "exhausted_reasoning": string
}`;

function buildFindingsUserMsg(session: ResearchSession, currentQuery: string, rawText: string): string {
  const complete = session.entries.filter(e => e.status === 'complete');
  const lines: string[] = [];
  lines.push(`PROBLEM STATEMENT:`);
  lines.push(session.problem_statement.trim());

  if (complete.length > 0) {
    lines.push('');
    lines.push('PRIOR COMPLETED TURNS (oldest first):');
    complete.forEach((e, i) => {
      lines.push(`Turn ${i + 1} query: ${e.query}`);
      lines.push(`Turn ${i + 1} findings: ${e.findings}`);
    });
  }

  lines.push('');
  lines.push(`THIS TURN'S QUERY: ${currentQuery}`);
  lines.push('');
  lines.push(`COUNSEL'S PASTED RESEARCH FOR THIS QUERY:`);
  lines.push(rawText.trim());

  return lines.join('\n');
}

function parseFindings(raw: string): RawFindings | null {
  try {
    const stripped = raw.trim()
      .replace(/^```(?:json)?/i, '')
      .replace(/```$/, '')
      .trim();
    const parsed = JSON.parse(stripped) as Record<string, unknown>;

    const findings           = parsed.findings;
    const exhausted          = parsed.exhausted;
    const exhaustedReasoning = parsed.exhausted_reasoning;

    if (
      typeof findings === 'string' && findings.trim() &&
      typeof exhausted === 'boolean' &&
      typeof exhaustedReasoning === 'string' && exhaustedReasoning.trim()
    ) {
      return {
        findings:            findings.trim(),
        exhausted,
        exhausted_reasoning: exhaustedReasoning.trim(),
      };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Synthesizes counsel's pasted-back research into the current
 * `'awaiting_findings'` entry, judges exhaustion, and sets the entry to
 * `'complete'`. Rolls `session.status` to `'exhausted'` if the verdict (or
 * the `MAX_RESEARCH_TURNS` safety valve) says so — same forced-cap
 * convention as the prior `runResearchTurn()`, just applied at submission
 * time instead of generation time.
 *
 * No-ops if the session is not `'active'`, if there is no entry currently
 * `'awaiting_findings'` (nothing to submit against), or if `rawText` is
 * empty.
 */
export async function submitFindings(session: ResearchSession, rawText: string): Promise<SubmitFindingsResult> {
  if (session.status !== 'active') {
    return unchanged(session);
  }
  const idx = session.entries.length - 1;
  const current = session.entries[idx];
  if (!current || current.status !== 'awaiting_findings') {
    return unchanged(session, 'No turn is currently awaiting findings.');
  }
  if (!rawText.trim()) {
    return unchanged(session, 'Paste in what you found before submitting.');
  }

  let raw: RawFindings | null;
  try {
    const { text } = await withRetry(() => callClaude({
      system:      FINDINGS_SYSTEM,
      userMsg:     buildFindingsUserMsg(session, current.query, rawText),
      maxTokens:   1400,
      skipLibrary: true,
    }));
    raw = parseFindings(text);
  } catch (e) {
    return unchanged(session, classifyError(e));
  }

  if (!raw) {
    return unchanged(session, 'Could not parse findings from AI response');
  }

  const now = new Date().toISOString();
  const willHitCap = session.entries.length >= MAX_RESEARCH_TURNS;

  const updatedEntry: ResearchQueryFinding = {
    ...current,
    findings:  raw.findings,
    status:    'complete',
    exhausted: raw.exhausted || willHitCap,
    exhausted_reasoning: willHitCap && !raw.exhausted
      ? `${raw.exhausted_reasoning} (forced — reached the maximum of ${MAX_RESEARCH_TURNS} research turns without an explicit exhaustion signal.)`
      : raw.exhausted_reasoning,
  };

  const entries = [...session.entries];
  entries[idx] = updatedEntry;

  const updatedSession: ResearchSession = {
    ...session,
    entries,
    status:     updatedEntry.exhausted ? 'exhausted' : 'active',
    updated_at: now,
  };

  return { session: updatedSession, advanced: true, ok: true };
}

// ─────────────────────────────────────────────────────────────────────────────
// generateFinalReport — 3–4 pages, OSCOLA footnotes
// ─────────────────────────────────────────────────────────────────────────────

const REPORT_SYSTEM = `You are Supo, the AFS Legal OS Research Assistant, writing up the final
report for a completed research session. You are given the problem
statement and every query/findings turn from the session — findings that
were themselves synthesized from research counsel actually pasted in.

Write a 3–4 page prose report (no bullet points, proper paragraphs) that
answers the problem statement, drawing on all the turns.

CITATION RULES — OSCOLA footnotes:
- Where a claim in the findings is traceable to a specific citable source
  (a case, statute, or text named in the findings), cite it as an OSCOLA
  footnote.
- Where a claim in the findings is general legal reasoning, synthesis
  across turns, or was never tied to a specific named source, leave it
  UNCITED. Do not invent a citation for it, and do not flag it — just write
  it as plain uncited prose. This is expected and normal, not an error.
- Never invent a case name, citation, year, or statute section that does
  not appear in the findings you were given.

FORMAT:
- Continuous prose, organized into clear paragraphs (headings optional and
  only if genuinely useful for a report this length).
- Footnotes numbered sequentially, referenced as superscript-style markers
  in the text (e.g. "...as established in the case.¹") with the footnote
  text itself listed at the end under a "Notes" heading, OSCOLA-formatted.
- End with a short concluding paragraph that directly answers the problem
  statement based on what was gathered — or states plainly what remains
  unresolved if the research did not fully answer it.

Return plain text only — no JSON, no markdown code fences.`;

function buildReportUserMsg(session: ResearchSession): string {
  const complete = session.entries.filter(e => e.status === 'complete');
  const lines: string[] = [];
  lines.push(`PROBLEM STATEMENT:`);
  lines.push(session.problem_statement.trim());
  lines.push('');
  lines.push(`RESEARCH GATHERED (${complete.length} turn${complete.length === 1 ? '' : 's'}, oldest first):`);
  complete.forEach((e, i) => {
    lines.push('');
    lines.push(`Turn ${i + 1} query: ${e.query}`);
    lines.push(`Turn ${i + 1} findings: ${e.findings}`);
  });
  return lines.join('\n');
}

/**
 * Generates the session's final report from every completed entry and
 * writes it to `session.final_report`. Never throws — a failed AI call or
 * a session with no completed entries degrades to `ok: false` with the
 * session returned unchanged.
 *
 * Does not require `session.status === 'exhausted'` — same reasoning as
 * the old draft-generation precondition: an `'abandoned'` session (counsel
 * stopped early) still has real findings worth reporting on. What's
 * required is at least one *completed* entry — a session with only an
 * `'awaiting_findings'` entry has nothing yet to report from.
 */
export async function generateFinalReport(session: ResearchSession): Promise<GenerateFinalReportResult> {
  const complete = session.entries.filter(e => e.status === 'complete');
  if (complete.length === 0) {
    return { session, ok: false, error: 'No completed research turns to report on yet.' };
  }

  let text: string;
  try {
    const result = await withRetry(() => callClaude({
      system:      REPORT_SYSTEM,
      userMsg:     buildReportUserMsg(session),
      maxTokens:   4000,
      skipLibrary: true,
    }));
    text = result.text.trim();
  } catch (e) {
    return { session, ok: false, error: classifyError(e) };
  }

  if (!text) {
    return { session, ok: false, error: 'Could not generate a report from AI response' };
  }

  const updatedSession: ResearchSession = {
    ...session,
    final_report: text,
    updated_at:   new Date().toISOString(),
  };

  return { session: updatedSession, ok: true };
}

// ─────────────────────────────────────────────────────────────────────────────
// SESSION FACTORY — convenience for 6d, not itself part of the core loop
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Creates a fresh, empty ResearchSession for a new problem statement.
 * Pure/synchronous — no AI call, no storage. `generateNextQuery()` is what
 * actually populates `entries`.
 */
export function createResearchSession(problemStatement: string): ResearchSession {
  const now = new Date().toISOString();
  return {
    id:                 uid(),
    problem_statement:  problemStatement.trim(),
    entries:            [],
    status:             'active',
    created_at:          now,
    updated_at:          now,
  };
}
